/**
 * activityStage — l'étape qu'une exécution d'activité montre, et la clé qui la fait entrer. Pur.
 *
 * `StudentActivityBody` pose une étape à la fois : l'activité relue, la correction, les
 * félicitations, le quiz de prérequis, sa relecture, l'entrée de l'activité (l'annonce et le décompte,
 * avant que le chrono parte), ou l'activité. D'une étape à l'autre, la page
 * sort et la suivante entre (`<StepStage>`) au lieu de couper sec ; une autre activité poussée par le
 * tuteur est une autre clé, elle entre aussi.
 */
import { type StudentActivityView } from "./useStudentActivityView"

export type ActivityStage = "finished" | "correction" | "congrats" | "quiz" | "review" | "intro" | "activity"

type StageInput = {
    finished: unknown
    correction: unknown
    congrats: unknown
    phase: string
    quizStatus: string
    reviewing: boolean
    /** L'entrée de l'activité est à jouer (`useStudentActivityView` en décide). */
    intro?: boolean
}

/** L'étape montrée, dans l'ordre où `StudentActivityBody` les essaie. */
export function activityStage(input: StageInput): ActivityStage {
    if (input.finished != null) return "finished"
    if (input.phase === "correction" && input.correction != null) return "correction"
    if (input.phase === "congrats" && input.congrats != null) return "congrats"
    // L'activité retirée par le tuteur : sa boîte « plus disponible » est sur la page d'activité, d'où
    // qu'elle parte (le quiz, l'entrée).
    if (input.phase === "deleted") return "activity"
    // Le quiz de prérequis pas encore passé prend la place de l'énoncé, puis sa relecture.
    if (input.quizStatus !== "passed") return "quiz"
    if (input.reviewing) return "review"
    if (input.intro === true) return "intro"
    return "activity"
}

/** Ce que lit `activityStage` dans la vue préparée par `useStudentActivityView`. */
export function activityStageOf(view: StudentActivityView): ActivityStage {
    return activityStage({
        finished: view.finished,
        correction: view.correction,
        congrats: view.congrats,
        phase: view.run.state.phase,
        quizStatus: view.quiz.state.status,
        reviewing: view.quiz.review.state.open != null,
        intro: view.intro != null,
    })
}

/** La clé de l'étape : son nom, et l'activité qu'elle porte (une autre activité entre aussi). */
export function activityStageKey(stage: ActivityStage, activityId: number | null | undefined): string {
    return `${stage}:${activityId ?? ""}`
}
